import {
	InputDate,
	InputField,
	InputSelect,
	Label,
	Modal,
	Paragraph,
	tokens,
	Tooltip,
} from '@welbe/web-design-system';
import { ReactComponent as ApproveSvg } from 'App/assets/img/approve_candidate.svg';
import WarningSvg from 'App/assets/img/warning.svg?url';
import { ModalConfirm } from 'App/Components/ModalConfirm/ModalConfirm';
import { Dispatch, SetStateAction } from 'react';
import { FiInfo } from 'react-icons/fi';
import { IoIosInformationCircle } from 'react-icons/io';
import { Candidate } from 'types';
import { ModalCustomAction } from '../ModalCustomAction/ModalCustomAction';
import { useModalApproveCandidate } from './ModalApproveCandidate.hooks';
import {
	Alert,
	Container,
	Footer,
	InputWrapper,
	MainFields,
} from './ModalApproveCandidate.styles';
import { Button } from '@welbe/design-system';

type ModalApproveCandidateProps = {
	selectedCandidates: Candidate[];
	setSelectedCandidates: Dispatch<SetStateAction<Candidate[]>>;
	setReloadTable: Dispatch<SetStateAction<boolean>>;
	handleClose: () => void;
};

export const ModalApproveCandidate = ({
	selectedCandidates,
	setSelectedCandidates,
	setReloadTable,
	handleClose,
}: ModalApproveCandidateProps): JSX.Element => {
	const {
		showModalConfirm,
		indexApproveCandidate,
		isLoadingApproveCandidate,
		validationError,
		confirmModal,
		handlShowModalConfirm,
		handleCloseModalConfirm,
		handleNextApproveCandidate,
		handleSelectedDate,
		handleChangeInputs,
		handleExitApprove,
		handleBranchOffice,
		handleInsurance,
		isLoadingBranchOffice,
		branchOfficeOptions,
		selectedBranchOffice,
		isLoadingInsurance,
		insuranceOptions,
		selectedInsurance,
		isCompanyBranchOfficesLoading,
		companyBranchOffices,
		selectedBusinessName,
		setSelectedBusinessName,
	} = useModalApproveCandidate({
		selectedCandidates,
		setSelectedCandidates,
		setReloadTable,
		handleClose,
	});

	return (
		<>
			<Modal open width="fit-content" headerIcon={<ApproveSvg />}>
				<Container>
					<Paragraph
						fontSize="18px"
						fontWeight="600"
						textAlign="center"
						margin="10px 0 0"
						color={tokens.color_primary}
					>
						Aprobar candidatos
					</Paragraph>
					<Alert>
						<IoIosInformationCircle color={tokens.color_primary} />
						<Paragraph>
							{`${indexApproveCandidate + 1}/${selectedCandidates?.length}`}{' '}
							Candidatos por aprobar
						</Paragraph>
					</Alert>
					<div>
						<Paragraph fontSize="16px" fontWeight="500">
							Completa la información para la aprobación del candidato
						</Paragraph>
						<Paragraph
							fontSize="18px"
							fontWeight="600"
							color={tokens.color_primary}
						>
							{selectedCandidates[indexApproveCandidate].name}
						</Paragraph>
					</div>
					<MainFields>
						<InputField
							id="companyBadgeNumber"
							label="Número de colaborador*"
							disabled={isLoadingApproveCandidate}
							placeholder={'Escribe el número de colaborador'}
							value={
								selectedCandidates[indexApproveCandidate].companyBadgeNumber
							}
							onChange={(value) =>
								handleChangeInputs('companyBadgeNumber', value)
							}
							error={!!validationError.companyBadgeNumber}
							errorText={validationError.companyBadgeNumber}
						/>
						<InputDate
							id="hiringDate"
							label="Fecha de ingreso*"
							disabled={isLoadingApproveCandidate}
							minDate={new Date('1980-12-20')}
							onChange={handleSelectedDate}
							value={selectedCandidates[indexApproveCandidate]?.hiringDate}
							error={!!validationError.hiringDate}
						/>
						<InputSelect
							id="branchOffice"
							placeholder="Selecione..."
							loading={isLoadingBranchOffice}
							label="Sucursal*"
							keyLabel="value"
							disabled={isLoadingApproveCandidate}
							options={branchOfficeOptions}
							onChange={handleBranchOffice}
							value={selectedBranchOffice}
							showEmpty={false}
							error={!!validationError.branchOfficeId}
							errorMessage={validationError.branchOfficeId}
						/>
						<InputField
							id="shift"
							label="Turno"
							disabled={isLoadingApproveCandidate}
							placeholder={'Escribe el turno'}
							value={selectedCandidates[indexApproveCandidate].shift}
							onChange={(value) => handleChangeInputs('shift', value)}
							error={!!validationError.shift}
							errorText={validationError.shift}
						/>
						<div>
							<InputWrapper>
								<Label margin="0">Razón social*</Label>
								<Tooltip title="El colaborador se asociará a esta Razón Social para fines de facturación">
									<FiInfo color={tokens.color_gray_dark} size={14} />
								</Tooltip>
							</InputWrapper>
							<InputSelect
								id="businessName"
								placeholder="Selecione..."
								loading={isCompanyBranchOfficesLoading}
								disabled={
									isCompanyBranchOfficesLoading ||
									companyBranchOffices.length < 2
								}
								options={companyBranchOffices}
								onChange={(data) => setSelectedBusinessName(data)}
								value={selectedBusinessName}
								error={!!validationError.businessName}
								errorMessage={validationError.businessName}
							/>
						</div>
						<InputField
							id="sector"
							label="Sector"
							disabled={isLoadingApproveCandidate}
							placeholder={'Escribe el sector'}
							value={selectedCandidates[indexApproveCandidate].sector}
							onChange={(value) => handleChangeInputs('sector', value)}
							error={!!validationError.sector}
							errorText={validationError.sector}
						/>
						<InputSelect
							id="insurance"
							placeholder="Selecione..."
							loading={isLoadingInsurance}
							label="Membresía*"
							keyLabel="value"
							showEmpty={false}
							disabled={isLoadingApproveCandidate}
							options={insuranceOptions}
							onChange={handleInsurance}
							value={selectedInsurance}
							error={!!validationError.insuranceId}
							errorMessage={validationError.insuranceId}
						/>
						<InputField
							id="position"
							label="Puesto"
							disabled={isLoadingApproveCandidate}
							placeholder="Escribe el puesto"
							value={selectedCandidates[indexApproveCandidate].position}
							onChange={(value) => handleChangeInputs('position', value)}
							error={!!validationError.position}
							errorText={validationError.position}
						/>
					</MainFields>
					<Footer>
						<Button
							id="cancel"
							key="1"
							type="button"
							disabled={isLoadingApproveCandidate}
							onClick={handlShowModalConfirm}
							width="150px"
							variant="outline"
						>
							Cancelar
						</Button>
						<Button
							id="confirm"
							key="2"
							type="button"
							disabled={isLoadingApproveCandidate}
							loading={isLoadingApproveCandidate}
							onClick={handleNextApproveCandidate}
							width="150px"
						>
							{indexApproveCandidate == selectedCandidates.length - 1
								? 'Finalizar'
								: 'Seguinte'}
						</Button>
					</Footer>
				</Container>
			</Modal>
			<ModalConfirm
				visible={confirmModal.show}
				onClose={confirmModal.onClose}
				imageUrl={confirmModal.icon}
				message={confirmModal.message}
			/>
			{showModalConfirm && (
				<ModalCustomAction
					icon={WarningSvg}
					titleText="Aprobación incompleta"
					descriptionText={`Aún no terminas de completar la información de todos los candidatos ¿Estás seguro de que quieres salir? Faltan ${
						selectedCandidates.length - indexApproveCandidate
					} candidatos`}
					selectedCandidates={selectedCandidates}
					handleClose={handleCloseModalConfirm}
					height="320px"
					zIndex={1200}
					buttons={[
						<Button
							key={'1'}
							type="button"
							onClick={handleCloseModalConfirm}
							width={'150px'}
							variant="outline"
						>
							Regresar
						</Button>,
						<Button
							key={'2'}
							type="button"
							onClick={handleExitApprove}
							width={'150px'}
						>
							Salir
						</Button>,
					]}
				/>
			)}
		</>
	);
};
